/**
 * The summary of one document: every value its latest reading gave and what became of each one,
 * and the place where the values of that reading still waiting for a person are decided.
 *
 * Expects the client id, the document id, which is null while the screen is closed and no request
 * is sent, the callback that closes the screen, `onGoToLine`, which the page gives to close the
 * screen and bring a line's field or list row into view, the documents list the page holds, the
 * callback that opens another document's summary in this screen, the one that opens a source
 * document's file, and the page's writes (useProfileFieldWrites).
 *
 * Renders the document's title, its stored file name and the name it arrived under, its tax year
 * and who added it when, the badge of where its reading stands (DocumentStatus, as on its row in
 * the documents table) when no status has values, otherwise one small label per status that has
 * values (summaryCountLabels). When
 * the documents list holds a later year's document of the same kind for the same legal entity, a
 * line says so and names it as a link to its summary (newerDocumentSentence). Under them the lines
 * grouped by the profile's sections (TAX_PROFILE_FIELD_GROUPS), then one group per list the document
 * gave values for, headed with the list table's name.
 *
 * Every line is a grid of three columns: the label, the value, and the status beside it (yellow
 * only for a value waiting for review). Under the value go the check it failed, who decided and
 * when, the page it was read on and a "Go to field" link. The value column shrinks and wraps first,
 * so the status stays in view at any width. In a list group the lines of one row (an owner, an
 * officer, a subsidiary, a state registration) sit in one card headed with the row's name and a
 * status for the whole row (summaryCardStatus); inside it the labels drop the row's name, and the
 * Name line is left out when its value is the card's heading and nothing waits on it. A row's
 * street, city, state and ZIP code (and the profile's own, with the second address line) are drawn
 * as one Address line (summaryAddressText) when they share a status; when one of them waits, the
 * Address line says so and the parts are drawn under it, each with its own line, so each part is
 * still decided on its own.
 *
 * A line waiting for review ('waiting') is decided here. To find every candidate of its field or
 * list cell, the screen reads the profile of the document's legal entity for the tax year of its
 * per-year lines (getProfile, the page's own read, keyed by entity and year), and the Value cell of
 * the line is the ListCell of the person window over that value: its ValuePicker lists every
 * candidate from every source, the approved value, "Enter a different value" and the check each
 * candidate failed, tags the options that came from this document "This document", and preselects
 * the newest candidate as everywhere on the page. A line whose preselected value is not this
 * document's says where the newer value came from under the cell (newerValueLine). Two lines of the
 * reading about one field share one cell. A waiting line the profile read has no value for (a
 * per-year value of a year other than the one read) keeps the read-only cell of the other lines.
 *
 * With a waiting line to decide, the footer holds Close and a yellow Finish review, which shows
 * Cancel and a green Confirm review. Confirm review sends every waiting line's choice at once
 * (cellReviewWrite in listRows.ts, reviewListRow in useProfileFieldWrites.tsx) to the document's
 * legal entity; the api's sentence for each refused value is drawn under its line and the screen
 * stays open. Every write refreshes this summary, the documents list and the profile, so the
 * decided lines move to their new status. With nothing to decide, the only button is Close.
 */

import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  TAX_PROFILE_FIELD_GROUPS,
  TAX_PROFILE_FIELDS,
  TAX_PROFILE_LIST_COLUMNS,
  taxProfileFieldByKey,
  taxProfileListColumnByKey,
} from '@shared/constants/taxProfileFields';
import { taxDocumentFileIsSpreadsheet } from '@shared/constants/taxProfile';
import type {
  TaxDocumentListItem,
  TaxDocumentReviewLine,
  TaxDocumentValueStatus,
  TaxProfileFieldValue,
  TaxProfileListName,
  TaxProfileResponse,
} from '@shared/types/taxProfile';
import { GenericModal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import useTaxProfile from '@/hooks/useTaxProfile';
import { formatFieldValue } from '@/pages/TaxProfile/format';
import { fieldNeedsReview, newestWaiting } from '@/pages/TaxProfile/fieldState';
import { cellReviewWrite, listCellTarget } from '@/pages/TaxProfile/listRows';
import {
  FieldWriteTarget,
  ProfileFieldWrites,
  TypedCellWrite,
  targetFromField,
} from '@/pages/TaxProfile/useProfileFieldWrites';
import {
  DOCUMENT_REVIEW_WORDS,
  DOCUMENT_VALUE_STATUS_WORDS,
  EMPTY_CELL_DASH,
  GO_TO_FIELD,
  LIST_GROUP_LABELS,
  NO_VALUES_SENTENCE,
  ROW_WORDS,
  SUMMARY_FAILED,
  SUMMARY_LOADING,
  addedAsSentence,
  checkFailedSentence,
  documentTitle,
  newerDocumentSentence,
  newerValueLine,
  SUMMARY_ADDRESS_LABEL,
  labelForYear,
  pageHint,
  summaryAddressText,
  summaryCardStatus,
  summaryCountLabels,
  summaryDecisionLine,
  summaryLineLabel,
  summaryYearAndAddedLine,
} from '@/pages/TaxProfile/wording';
import DocumentStatus from '@/pages/TaxProfile/components/DocumentStatus';
import { ListCell } from '@/pages/TaxProfile/components/ListTable';
import SourceText from '@/pages/TaxProfile/components/SourceText';
import type {
  FieldDecision,
  PickerDefinition,
} from '@/pages/TaxProfile/components/ValuePicker';

/** The text class of each status beside a value: yellow only for a value that waits for a person. */
const STATUS_CLASS: Readonly<Record<TaxDocumentValueStatus, string>> = {
  waiting: 'text-warning font-semibold',
  accepted: 'text-success font-semibold',
  same_as_approved: 'opacity-70',
  not_used: 'opacity-70',
  replaced: 'opacity-70',
  not_captured: 'opacity-70',
};

/** The badge class of each count label at the top of the screen. */
const COUNT_LABEL_CLASS: Readonly<Record<TaxDocumentValueStatus, string>> = {
  waiting: 'badge-warning',
  accepted: 'badge-success',
  same_as_approved: 'badge-ghost',
  not_used: 'badge-ghost',
  replaced: 'badge-ghost',
  not_captured: 'badge-ghost',
};

/**
 * The columns of every line: the label between 6rem and 8rem, the value in what is left (down to
 * nothing, so it wraps rather than pushing the next column out), and the status as wide as its
 * words.
 */
const LINE_GRID =
  'grid grid-cols-[minmax(6rem,8rem)_minmax(0,1fr)_auto] items-start gap-x-3';

const LIST_ORDER: ReadonlyArray<TaxProfileListName> = [
  'owners',
  'officers',
  'subsidiaries',
  'stateTaxIds',
];

/**
 * Where each key sits in the catalog or, for a key the catalog does not have, in
 * TAX_PROFILE_LIST_COLUMNS, built once when the file loads rather than searched for on every
 * comparison of a sort. A key keeps the first place it is found at.
 */
const KEY_ORDER = new Map<string, number>();
[TAX_PROFILE_FIELDS, TAX_PROFILE_LIST_COLUMNS].forEach((definitions) =>
  definitions.forEach(({ key }, index) => {
    if (!KEY_ORDER.has(key)) KEY_ORDER.set(key, index);
  })
);

/** Where a key sits in the catalog or in TAX_PROFILE_LIST_COLUMNS, for the order of a group. */
const orderOfKey = (key: string | null): number =>
  (key === null ? undefined : KEY_ORDER.get(key)) ?? Number.MAX_SAFE_INTEGER;

/**
 * The lines of one group in the order the page lists them: a section's fields in catalog order;
 * a list's by row name, then column order, then tax year, the answers nothing was recorded for
 * last.
 */
const inPageOrder = (lines: TaxDocumentReviewLine[]) =>
  [...lines].sort(
    (one, other) =>
      Number(one.status === 'not_captured') -
        Number(other.status === 'not_captured') ||
      (one.rowName ?? '').localeCompare(other.rowName ?? '') ||
      orderOfKey(one.key) - orderOfKey(other.key) ||
      (one.taxYear ?? 0) - (other.taxYear ?? 0)
  );

type LineGroup = {
  id: string;
  label: string;
  /** The list the group's lines are on; null for a section of the profile's own fields. */
  list: TaxProfileListName | null;
  lines: TaxDocumentReviewLine[];
};

/** The lines grouped under the profile's section headings, then under the list tables' names. */
const groupsOf = (lines: TaxDocumentReviewLine[]): LineGroup[] => {
  const sections = TAX_PROFILE_FIELD_GROUPS.map((group) => ({
    id: group.key,
    label: group.label,
    list: null,
    lines: inPageOrder(
      lines.filter(
        (line) =>
          line.list === null &&
          line.key !== null &&
          taxProfileFieldByKey(line.key)?.group === group.key
      )
    ),
  }));
  const lists = LIST_ORDER.map((list) => ({
    id: list,
    label: LIST_GROUP_LABELS[list],
    list,
    lines: inPageOrder(lines.filter((line) => line.list === list)),
  }));
  return [...sections, ...lists].filter((group) => group.lines.length > 0);
};

/** The value in the shape its field or column calls for, or a dash. */
const valueText = (line: TaxDocumentReviewLine): string => {
  if (line.value === null || line.value === '') return EMPTY_CELL_DASH;
  const definition = line.key
    ? taxProfileFieldByKey(line.key) ?? taxProfileListColumnByKey(line.key)
    : undefined;
  return definition ? formatFieldValue(line.value, definition) : line.value;
};

/** The lines of one list row, drawn as one card headed with the row's name. */
type RowCard = {
  id: string;
  recordId: number | null;
  name: string;
  lines: TaxDocumentReviewLine[];
};

/**
 * The lines of a list group by the row they are on, in the order the group lists them: by the row
 * id, and an answer nothing was recorded for (no row id) by the row's name.
 */
const cardsOf = (lines: TaxDocumentReviewLine[]): RowCard[] => {
  const cards: RowCard[] = [];
  lines.forEach((line) => {
    const name = line.rowName ?? '';
    const card = cards.find((kept) =>
      line.recordId === null
        ? kept.name === name
        : kept.recordId === line.recordId ||
          (kept.recordId === null && kept.name === name)
    );
    if (card) {
      card.lines.push(line);
      if (card.recordId === null) card.recordId = line.recordId;
    } else {
      cards.push({
        id: line.recordId === null ? `name-${name}` : String(line.recordId),
        recordId: line.recordId,
        name,
        lines: [line],
      });
    }
  });
  return cards;
};

/** The list columns that hold a row's name, which the heading of the row's card already shows. */
const ROW_NAME_KEYS: ReadonlySet<string> = new Set([
  'owner_full_legal_name',
  'officer_full_legal_name',
  'subsidiary_name',
  'state_registration_state',
]);

type AddressPart = 'street' | 'line2' | 'city' | 'state' | 'zip';

/** The fields and list columns that are drawn together as one Address line, by their part. */
const ADDRESS_PART_OF_KEY: ReadonlyMap<string, AddressPart> = new Map([
  ['street_address', 'street'],
  ['address_line_2', 'line2'],
  ['city', 'city'],
  ['state', 'state'],
  ['zip_code', 'zip'],
  ['owner_street_address', 'street'],
  ['owner_city', 'city'],
  ['owner_state', 'state'],
  ['owner_zip_code', 'zip'],
]);

/** What a group or a card draws: one line, or the parts of an address drawn as one line. */
type SummaryEntry =
  | { kind: 'line'; line: TaxDocumentReviewLine }
  | { kind: 'address'; id: string; parts: TaxDocumentReviewLine[] };

/**
 * What a section or a card draws, in the order of its lines. In a card (`rowName` given) the Name
 * line is left out when its value is the card's heading and it does not wait. Two or more address
 * parts, each given once, become one Address line where the first of them was, when they share a
 * status or one of them waits; otherwise each part stays its own line.
 */
const entriesOf = (
  lines: TaxDocumentReviewLine[],
  rowName: string | null,
  id: string
): SummaryEntry[] => {
  const kept =
    rowName === null
      ? lines
      : lines.filter(
          (line) =>
            !(
              line.key !== null &&
              ROW_NAME_KEYS.has(line.key) &&
              line.status !== 'waiting' &&
              (line.value ?? '').trim() === rowName.trim()
            )
        );
  const parts = kept.filter(
    (line) => line.key !== null && ADDRESS_PART_OF_KEY.has(line.key)
  );
  const statuses = new Set(parts.map((line) => line.status));
  const fold =
    parts.length >= 2 &&
    new Set(parts.map((line) => line.key)).size === parts.length &&
    (statuses.has('waiting') || statuses.size === 1);
  if (!fold) return kept.map((line) => ({ kind: 'line', line }));
  const inAddressOrder = [...parts].sort(
    (one, other) => orderOfKey(one.key) - orderOfKey(other.key)
  );
  return kept.flatMap((line): SummaryEntry[] => {
    if (!parts.includes(line)) return [{ kind: 'line', line }];
    return line === parts[0]
      ? [{ kind: 'address', id, parts: inAddressOrder }]
      : [];
  });
};

/** Each string once, in the order first given. */
const distinct = (texts: (string | null)[]): string[] => [
  ...new Set(texts.filter((text): text is string => Boolean(text))),
];

/**
 * True for a line the page has a place for: a catalog field, or a list cell on a row the page lists.
 * An answer nothing was recorded for is on no row.
 */
export const lineHasPlace = (line: TaxDocumentReviewLine): boolean =>
  line.key !== null && (line.list === null || line.recordId !== null);

/**
 * True for a waiting line that names a stored row, a key and, for a list value, a list row: the
 * lines this screen decides.
 */
const lineIsDecidable = (line: TaxDocumentReviewLine): boolean =>
  line.status === 'waiting' && line.rowId !== null && lineHasPlace(line);

/** True when the line's field or list column is kept per tax year, so its year picks its value. */
const lineIsPerYear = (line: TaxDocumentReviewLine): boolean => {
  if (line.key === null) return false;
  const field = taxProfileFieldByKey(line.key);
  if (field) return field.yearly === true;
  const column = taxProfileListColumnByKey(line.key);
  return column?.yearly === true || Boolean(column?.taxYearColumn);
};

/**
 * The tax year to read the profile for: the year most of the waiting per-year lines are for, else
 * the document's own year, else null (the api then answers with the entity's newest year, which
 * the lines that are not per year do not depend on).
 */
const profileYearOf = (
  lines: TaxDocumentReviewLine[],
  documentYear: number | null
): number | null => {
  const perYear = new Map<number, number>();
  lines.filter(lineIsPerYear).forEach((line) => {
    if (line.taxYear !== null)
      perYear.set(line.taxYear, (perYear.get(line.taxYear) ?? 0) + 1);
  });
  const [mostLines] = [...perYear].sort(
    ([oneYear, one], [otherYear, other]) => other - one || otherYear - oneYear
  );
  return mostLines ? mostLines[0] : documentYear;
};

/** Where a value is written: its key, its list row and its tax year, as one string. */
const addressOf = (
  key: string,
  recordId: number | null | undefined,
  taxYear: number | null | undefined
) => `${key}|${recordId ?? ''}|${taxYear ?? ''}`;

/** A waiting line's value in the profile read, with what writing it needs. */
type LineValue = {
  address: string;
  field: TaxProfileFieldValue;
  definition: PickerDefinition;
  /** The write target, with no acceptRowId (cellReviewWrite adds it). */
  target: FieldWriteTarget;
};

/**
 * The value of the profile read that a waiting line is a candidate for: the catalog field, or the
 * cell of the list row, for the line's tax year where the value is per year. Null when the read has
 * no such value, or the value has nothing waiting.
 */
const valueOfLine = (
  line: TaxDocumentReviewLine,
  profile: TaxProfileResponse
): LineValue | null => {
  if (line.key === null) return null;
  const perYear = lineIsPerYear(line);
  const sameYear = (field: TaxProfileFieldValue) =>
    !perYear || (field.taxYear ?? profile.taxYear) === line.taxYear;

  let found: Omit<LineValue, 'address'> | null = null;
  if (line.list === null) {
    const definition = taxProfileFieldByKey(line.key);
    const field = profile.fields.find((entry) => entry.key === line.key);
    if (definition && field && sameYear(field)) {
      // targetFromField names the newest candidate as acceptRowId; cellReviewWrite picks it again.
      const { acceptRowId, ...target } = targetFromField(
        field,
        definition,
        profile.taxYear
      );
      found = { field, definition, target };
    }
  } else {
    const column = taxProfileListColumnByKey(line.key);
    const row = profile[line.list].find(
      (entry) => entry.recordId === line.recordId
    );
    const cell = row?.cells[line.key];
    if (column && row && cell && sameYear(cell)) {
      found = {
        field: cell,
        definition: column,
        target: listCellTarget(row, column, cell, profile.taxYear),
      };
    }
  }
  if (!found || !fieldNeedsReview(found.field)) return null;
  return {
    ...found,
    address: addressOf(
      found.target.definition.key,
      found.target.recordId,
      found.target.taxYear
    ),
  };
};

/**
 * The document the line at the top of the screen names: of the documents that were read, the one
 * of the same kind and the same legal entity with the latest year after this document's. Null when
 * there is none, or when this document has no kind, year or entity to compare.
 */
const newerDocumentOf = (
  document: TaxDocumentListItem,
  documents: TaxDocumentListItem[]
): TaxDocumentListItem | null => {
  const { doc_type: kind, doc_year: year, legal_entity_id: entity } = document;
  if (kind == null || year == null || entity == null) return null;
  return (
    documents
      .filter(
        (other) =>
          other.id !== document.id &&
          other.doc_type === kind &&
          other.legal_entity_id === entity &&
          other.doc_year != null &&
          other.doc_year > year &&
          other.extraction_status === 'extracted'
      )
      .sort((one, other) => (other.doc_year ?? 0) - (one.doc_year ?? 0))[0] ??
    null
  );
};

export interface ReviewModalProps {
  clientId: number;
  /** The document summarized. Null renders nothing and asks the api for nothing. */
  documentId: number | null;
  onClose: () => void;
  /** Closes the screen and brings the line's field or list row into view on the page. */
  onGoToLine: (line: TaxDocumentReviewLine) => void;
  /** The documents list the page holds, where a newer document of the same kind is looked for. */
  documents: TaxDocumentListItem[];
  /** Shows another document's summary in this screen. */
  onOpenDocumentSummary: (documentId: number) => void;
  /** Opens a source document's file, from a document's name in a picker or under a line. */
  onOpenSourceDocument?: (documentId: number) => void;
  writes: Pick<ProfileFieldWrites, 'reviewListRow' | 'isWriting'>;
}

/**
 * The screen of one document. Keyed by the document id in ReviewModal, so the choices made on one
 * document's screen are dropped when the screen moves to another document.
 */
function DocumentReview({
  clientId,
  documentId,
  onClose,
  onGoToLine,
  documents,
  onOpenDocumentSummary,
  onOpenSourceDocument,
  writes,
}: ReviewModalProps & { documentId: number }) {
  const { getDocumentReview, getProfile } = useTaxProfile();
  const { data: review, error } = getDocumentReview(clientId, documentId);

  const decidableLines = (review?.lines ?? []).filter(lineIsDecidable);
  const entityId =
    review?.document.legal_entity_id ??
    decidableLines.find((line) => line.legalEntityId !== null)?.legalEntityId ??
    null;
  const profileYear = profileYearOf(
    decidableLines,
    review?.document.doc_year ?? null
  );
  // With no line to decide the screen draws no ValuePicker, so the profile is not fetched.
  const { data: profileAnswer } = getProfile(clientId, entityId, profileYear, {
    skip: decidableLines.length === 0,
  });
  // getProfile keeps the previous answer while a new key loads, so an answer for another entity or
  // year is not used.
  const profile =
    profileAnswer &&
    (entityId === null || profileAnswer.legalEntity?.id === entityId) &&
    (profileYear === null || profileAnswer.taxYear === profileYear)
      ? profileAnswer
      : undefined;

  /** The choice made in each value's ValuePicker, by addressOf. */
  const [decisions, setDecisions] = useState<Record<string, FieldDecision>>({});
  /** The line whose ValuePicker is open, by its rowId; null while none is. */
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  /** True once Finish review was clicked, which shows Cancel and Confirm review. */
  const [confirming, setConfirming] = useState(false);
  /** The api's sentence for each value refused at the last Confirm review, by addressOf. */
  const [failures, setFailures] = useState<Record<string, string>>({});

  const valuesByRowId = new Map<number, LineValue>();
  if (profile) {
    decidableLines.forEach((line) => {
      const value = valueOfLine(line, profile);
      if (value && line.rowId !== null) valuesByRowId.set(line.rowId, value);
    });
  }
  /** Each value once, however many lines of the reading are about it. */
  const values = [
    ...new Map(
      [...valuesByRowId.values()].map((value) => [value.address, value])
    ).values(),
  ];
  const busy = writes.isWriting;

  const confirm = async () => {
    const decided: FieldWriteTarget[] = [];
    const typed: TypedCellWrite[] = [];
    values.forEach(({ address, field, target }) => {
      const write = cellReviewWrite(target, field, decisions[address]);
      if (!write) return;
      if ('typed' in write) typed.push(write.typed);
      else decided.push(write.decided);
    });
    const refused = await writes.reviewListRow(decided, typed, entityId);
    setConfirming(false);
    setOpenRowId(null);
    const refusedByAddress = Object.fromEntries(
      refused.map((failure) => [
        addressOf(failure.key, failure.recordId, failure.taxYear),
        failure.error,
      ])
    );
    setFailures(refusedByAddress);
    // The choices of the values the api refused stay, so Finish review offers them again.
    setDecisions((kept) =>
      Object.fromEntries(
        Object.entries(kept).filter(([address]) => address in refusedByAddress)
      )
    );
  };

  // A value read out of a spreadsheet sits on a sheet rather than on a page, and the number comes
  // back in the same field either way, so the file's name is what tells the two apart.
  const isSpreadsheet = taxDocumentFileIsSpreadsheet(
    review?.document.original_filename ??
      review?.document.onedrive_filename ??
      ''
  );

  /** The Value cell of a waiting line decided here: its ListCell, and what is said under it. */
  const decidableCell = (
    line: TaxDocumentReviewLine,
    rowId: number,
    value: LineValue
  ) => {
    const { address, field, definition } = value;
    const label = summaryLineLabel(line);
    const newest = newestWaiting(field);
    const newerSource =
      newest && !newest.sources.some((source) => source.rowId === rowId)
        ? newest.sources[0] ?? null
        : null;
    const refusal = failures[address];
    return (
      <>
        <ListCell
          column={{ key: definition.key, header: label }}
          definition={definition}
          cell={field}
          decision={decisions[address]}
          rowWaiting
          isOpen={openRowId === rowId}
          onOpenChange={(open) => setOpenRowId(open ? rowId : null)}
          onDecide={(decision) =>
            setDecisions((kept) => ({ ...kept, [address]: decision }))
          }
          busy={busy}
          onOpenSourceDocument={onOpenSourceDocument}
          testId={`summary-cell-${rowId}`}
          note={DOCUMENT_REVIEW_WORDS.pickerNote}
          thisDocumentId={documentId}
        />
        {newerSource && (
          <SourceText
            pieces={newerValueLine(newerSource, definition.key)}
            onOpenDocument={onOpenSourceDocument}
            className='block text-xs opacity-70 mt-1'
          />
        )}
        {refusal && (
          <p
            className='mt-1 text-xs text-error'
            data-testid={`summary-error-${rowId}`}
          >
            {refusal}
          </p>
        )}
      </>
    );
  };

  /** Where each line is in the review's answer, which names the test id of a line with no row. */
  const answerIndex = new Map(
    (review?.lines ?? []).map((line, index) => [line, index])
  );

  /** The status beside a value: a check before Accepted, the whole sentence on hover. */
  const statusWords = (status: TaxDocumentValueStatus) => {
    const words = DOCUMENT_VALUE_STATUS_WORDS[status];
    return (
      <span
        className={`text-xs whitespace-nowrap ${STATUS_CLASS[status]}`}
        title={words.sentence}
      >
        {status === 'accepted' && (
          <FontAwesomeIcon icon={faCheck} className='mr-1' />
        )}
        {words.badge}
      </span>
    );
  };

  /** The small line at the foot of a value: the pages it was read on and "Go to field". */
  const lineFoot = (
    goTo: TaxDocumentReviewLine,
    pages: string[],
    label: string
  ) => {
    const hasPlace = lineHasPlace(goTo);
    if (pages.length === 0 && !hasPlace) return null;
    return (
      <span className='mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs'>
        {/* nowrap so "Page 1" moves to the next line whole rather than breaking inside. */}
        {pages.map((page) => (
          <span key={page} className='opacity-70 whitespace-nowrap'>
            {page}
          </span>
        ))}
        {hasPlace && (
          <button
            type='button'
            className='link link-primary'
            aria-label={`${GO_TO_FIELD}: ${label}`}
            onClick={() => onGoToLine(goTo)}
          >
            {GO_TO_FIELD}
          </button>
        )}
      </span>
    );
  };

  /** One line: its label, its value with what is said under it, and its status beside it. */
  const renderLine = (line: TaxDocumentReviewLine) => {
    const page = pageHint(line.page, isSpreadsheet);
    const decision = summaryDecisionLine(line);
    const { rowId } = line;
    const value = rowId !== null ? valuesByRowId.get(rowId) ?? null : null;
    const id = rowId ?? `answer-${answerIndex.get(line) ?? 0}`;
    return (
      <div
        key={`line-${id}`}
        data-testid={`summary-line-${id}`}
        className={LINE_GRID}
      >
        <span className='text-sm opacity-80'>
          {labelForYear(line.label, line.taxYear)}
        </span>
        <div className='min-w-0 text-sm' data-testid={`summary-value-${id}`}>
          {value && rowId !== null ? (
            decidableCell(line, rowId, value)
          ) : (
            <span>{valueText(line)}</span>
          )}
          {line.checkFailedReason && (
            <span className='block text-xs text-warning mt-1'>
              {checkFailedSentence(line.checkFailedReason)}
            </span>
          )}
          {decision && (
            <span className='block text-xs opacity-70 mt-1'>{decision}</span>
          )}
          {lineFoot(line, page ? [page] : [], summaryLineLabel(line))}
        </div>
        <div className='justify-self-end' data-testid={`summary-status-${id}`}>
          {statusWords(line.status)}
        </div>
      </div>
    );
  };

  /**
   * A row's (or the profile's) street, city, state and ZIP code as one Address line. When a part
   * waits, the line's status says so and every part is drawn under it with its own line, where the
   * waiting ones are decided; otherwise the parts' checks, decisions and pages go under the line.
   */
  const renderAddress = (entry: Extract<SummaryEntry, { kind: 'address' }>) => {
    const { id, parts } = entry;
    const waiting = parts.some((part) => part.status === 'waiting');
    const status = waiting ? 'waiting' : parts[0].status;
    const texts: Partial<Record<AddressPart, string>> = {};
    parts.forEach((part) => {
      const which = ADDRESS_PART_OF_KEY.get(part.key ?? '');
      if (which && part.value) texts[which] = valueText(part);
    });
    const text = summaryAddressText(texts) || EMPTY_CELL_DASH;
    const label = summaryLineLabel({
      label: SUMMARY_ADDRESS_LABEL,
      taxYear: null,
      rowName: parts[0].rowName,
    });
    return (
      <div key={`address-${id}`} data-testid={`summary-address-${id}`}>
        <div className={LINE_GRID} data-testid={`summary-address-line-${id}`}>
          <span className='text-sm opacity-80'>{SUMMARY_ADDRESS_LABEL}</span>
          <div className='min-w-0 text-sm'>
            <span>{text}</span>
            {!waiting && (
              <>
                {distinct(
                  parts.map((part) =>
                    part.checkFailedReason
                      ? checkFailedSentence(part.checkFailedReason)
                      : null
                  )
                ).map((sentence) => (
                  <span
                    key={sentence}
                    className='block text-xs text-warning mt-1'
                  >
                    {sentence}
                  </span>
                ))}
                {distinct(parts.map(summaryDecisionLine)).map((decision) => (
                  <span
                    key={decision}
                    className='block text-xs opacity-70 mt-1'
                  >
                    {decision}
                  </span>
                ))}
                {lineFoot(
                  parts[0],
                  distinct(
                    parts.map((part) => pageHint(part.page, isSpreadsheet))
                  ),
                  label
                )}
              </>
            )}
          </div>
          <div
            className='justify-self-end'
            data-testid={`summary-address-status-${id}`}
          >
            {statusWords(status)}
          </div>
        </div>
        {waiting && (
          <div
            className='mt-2 ml-3 flex flex-col gap-2 border-l-2 border-warning/40 pl-3'
            data-testid={`summary-address-parts-${id}`}
          >
            {parts.map(renderLine)}
          </div>
        )}
      </div>
    );
  };

  const renderEntries = (entries: SummaryEntry[]) => (
    <div className='flex flex-col gap-2'>
      {entries.map((entry) =>
        entry.kind === 'line' ? renderLine(entry.line) : renderAddress(entry)
      )}
    </div>
  );

  /** A list row's card: its name and status on top, then its lines without the name. */
  const renderCard = (
    list: TaxProfileListName,
    groupId: string,
    card: RowCard
  ) => {
    const waitingCount = card.lines.filter(
      (line) => line.status === 'waiting'
    ).length;
    const id = `${groupId}-${card.id}`;
    return (
      <div
        key={id}
        data-testid={`summary-card-${id}`}
        className={`rounded-lg border p-3 ${
          waitingCount > 0 ? 'border-warning' : 'border-base-300'
        }`}
      >
        <div className='mb-2 flex items-baseline justify-between gap-3'>
          <span className='min-w-0 font-bold'>{card.name}</span>
          <span
            className={`text-xs whitespace-nowrap ${
              waitingCount > 0 ? 'text-warning font-semibold' : 'opacity-70'
            }`}
            data-testid={`summary-card-status-${id}`}
          >
            {summaryCardStatus(list, waitingCount)}
          </span>
        </div>
        {renderEntries(entriesOf(card.lines, card.name, id))}
      </div>
    );
  };

  /** A list group's cards, one per row, in the order the group lists the rows. */
  const renderCards = (
    list: TaxProfileListName,
    groupId: string,
    lines: TaxDocumentReviewLine[]
  ) => (
    <div className='flex flex-col gap-2'>
      {cardsOf(lines).map((card) => renderCard(list, groupId, card))}
    </div>
  );

  const groups = review ? groupsOf(review.lines) : [];
  const counts = review ? summaryCountLabels(review.counts) : [];
  const addedAs = review ? addedAsSentence(review.document) : null;
  const newer = review ? newerDocumentOf(review.document, documents) : null;
  /** True while the profile read the waiting lines are decided over is on its way. */
  const profileLoading = decidableLines.length > 0 && !profile;
  const hasSomethingToDecide = values.length > 0 || profileLoading;

  const footer = () => {
    const close = (
      <button type='button' className='btn btn-sm' onClick={onClose}>
        {DOCUMENT_REVIEW_WORDS.close}
      </button>
    );
    if (!hasSomethingToDecide) return close;
    if (confirming) {
      return (
        <>
          <button
            type='button'
            className='btn btn-sm btn-ghost'
            onClick={() => setConfirming(false)}
          >
            {ROW_WORDS.cancel}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-success'
            disabled={busy}
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              confirm();
            }}
          >
            {ROW_WORDS.confirmReview}
          </button>
        </>
      );
    }
    return (
      <>
        {close}
        <button
          type='button'
          className='btn btn-sm btn-warning'
          disabled={busy || profileLoading}
          onClick={() => {
            setOpenRowId(null);
            setConfirming(true);
          }}
        >
          {ROW_WORDS.finishReview}
        </button>
      </>
    );
  };

  return (
    // [overflow-wrap:anywhere] is inherited by everything in the modal, so a long file name or a
    // value with no spaces breaks inside its line or cell instead of widening the modal.
    <GenericModal
      isOpen
      onClose={onClose}
      className='max-w-4xl [overflow-wrap:anywhere]'
    >
      {!review && !error && (
        <div className='flex items-center gap-2 p-2'>
          <Spinner />
          {SUMMARY_LOADING}
        </div>
      )}
      {error && (
        <p className='text-error p-2'>{error.message || SUMMARY_FAILED}</p>
      )}

      {review && (
        <>
          <h3 className='font-bold text-lg'>
            {documentTitle(review.document)}
          </h3>
          <div className='mb-3 flex flex-col gap-0.5'>
            <p className='text-xs opacity-70'>
              {review.document.onedrive_filename ??
                review.document.original_filename}
            </p>
            {addedAs && <p className='text-xs opacity-70'>{addedAs}</p>}
            <p className='text-xs opacity-70'>
              {summaryYearAndAddedLine(review.document)}
            </p>
          </div>

          <div className='mb-3 flex flex-col gap-2'>
            {/* The count labels say what the badge would ("41 values to review") and more, so the
                badge shows only when there are none: a reading still running, failed, or empty. */}
            {counts.length === 0 && (
              <DocumentStatus document={review.document} />
            )}
            {counts.length > 0 && (
              <div
                className='flex flex-wrap gap-1.5'
                data-testid='summary-counts'
              >
                {counts.map(({ status, text }) => (
                  <span
                    key={status}
                    className={`badge badge-sm ${COUNT_LABEL_CLASS[status]}`}
                    data-testid={`summary-count-${status}`}
                  >
                    {text}
                  </span>
                ))}
              </div>
            )}
            {counts.length === 0 &&
              review.document.extraction_status === 'extracted' && (
                <p className='text-sm'>{NO_VALUES_SENTENCE}</p>
              )}
            {newer && (
              <div
                className='rounded-lg bg-base-200 p-2 text-sm'
                data-testid='summary-newer-document'
              >
                <SourceText
                  pieces={newerDocumentSentence(newer)}
                  onOpenDocument={onOpenDocumentSummary}
                />
              </div>
            )}
          </div>

          {groups.length > 0 && (
            // No overflow wrapper around the groups or the cards, so an open ValuePicker hangs out
            // of its line and stays inside the modal-box (ValuePicker's visibleArea).
            <div className='flex flex-col gap-4'>
              {groups.map((group) => (
                <section
                  key={group.id}
                  data-testid={`summary-group-${group.id}`}
                >
                  <h4 className='mb-2 rounded bg-base-200 px-2 py-1 text-sm font-bold'>
                    {group.label}
                  </h4>
                  {group.list === null ? (
                    <div className='px-2'>
                      {renderEntries(entriesOf(group.lines, null, group.id))}
                    </div>
                  ) : (
                    renderCards(group.list, group.id, group.lines)
                  )}
                </section>
              ))}
            </div>
          )}

          {Object.keys(failures).length > 0 && (
            <p className='mt-4 text-sm text-error text-right'>
              {DOCUMENT_REVIEW_WORDS.refused}
            </p>
          )}
          <div className='mt-4 flex justify-end gap-2'>{footer()}</div>
        </>
      )}
    </GenericModal>
  );
}

export default function ReviewModal(props: ReviewModalProps) {
  const { documentId } = props;
  if (documentId === null) return null;
  return <DocumentReview key={documentId} {...props} documentId={documentId} />;
}
